import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, QrCode, Wifi } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export function PublicReportCard({ propertyId }) {
  const [token, setToken] = useState("");
  const [gb, setGb] = useState({ wifi_name: "", wifi_password: "", checkout_time: "", house_rules: "", welcome_note: "" });

  useEffect(() => {
    api.get(`/properties/${propertyId}/public-link`).then((r) => setToken(r.data.token)).catch(() => {});
    api.get(`/properties/${propertyId}`).then((r) => {
      const p = r.data;
      setGb({ wifi_name: p.wifi_name || "", wifi_password: p.wifi_password || "", checkout_time: p.checkout_time || "", house_rules: p.house_rules || "", welcome_note: p.welcome_note || "" });
    }).catch(() => {});
  }, [propertyId]);

  if (!token) return null;
  const url = `${window.location.origin}/report/${token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy automatically — select the link and copy it"); }
  };
  const saveGb = async () => {
    try { await api.put(`/properties/${propertyId}`, gb); toast.success("Guest info saved"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-border rounded-md p-5 mb-5" data-testid="public-report-card">
      <div className="flex items-start gap-5 flex-wrap">
        <div className="bg-white p-2 border border-border rounded-md"><QRCodeSVG value={url} size={120} /></div>
        <div className="flex-1 min-w-[220px]">
          <p className="overline flex items-center gap-1.5"><QrCode className="h-3.5 w-3.5" />Guest welcome & issue-report link</p>
          <p className="text-sm text-muted-foreground mt-1">Print the QR for the guestbook or share the link. Guests see your welcome info and can report issues (name, photos) straight into your Issues list — no login needed.</p>
          <div className="flex items-center gap-2 mt-3">
            <input readOnly value={url} data-testid="public-report-url" className="flex-1 text-xs px-3 py-2 border border-border rounded-md bg-secondary/40" />
            <Button size="sm" variant="outline" data-testid="copy-report-link" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
          </div>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-border">
        <p className="overline flex items-center gap-1.5 mb-3"><Wifi className="h-3.5 w-3.5" />Guest welcome info (shown on the report page)</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="overline">WiFi network</Label><Input data-testid="gb-wifi-name" value={gb.wifi_name} onChange={(e) => setGb({ ...gb, wifi_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">WiFi password</Label><Input data-testid="gb-wifi-pass" value={gb.wifi_password} onChange={(e) => setGb({ ...gb, wifi_password: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Check-out time</Label><Input data-testid="gb-checkout" value={gb.checkout_time} onChange={(e) => setGb({ ...gb, checkout_time: e.target.value })} placeholder="e.g. 10:00am" className="mt-1.5" /></div>
          <div><Label className="overline">Welcome note</Label><Input data-testid="gb-welcome" value={gb.welcome_note} onChange={(e) => setGb({ ...gb, welcome_note: e.target.value })} className="mt-1.5" /></div>
        </div>
        <div className="mt-3"><Label className="overline">House rules</Label><Textarea data-testid="gb-rules" value={gb.house_rules} onChange={(e) => setGb({ ...gb, house_rules: e.target.value })} rows={2} className="mt-1.5" /></div>
        <Button size="sm" className="mt-3" data-testid="gb-save" onClick={saveGb}>Save guest info</Button>
      </div>
    </div>
  );
}
